import api from './api'

export interface AdminWaitlistEntry {
  id: string
  email: string
  source: 'email_otp' | 'google_oauth' | 'direct'
  marketingOptIn: boolean
  approvedAt: string | null
  confirmedInterestAt: string | null
  createdAt: string
  updatedAt: string
}

export async function setWaitlistAccess(id: string, approved: boolean): Promise<AdminWaitlistEntry> {
  const { data } = await api.patch(`/admin/waitlist/${id}/access`, { approved })
  return data.data
}

export async function deleteWaitlistEntries(ids: string[]): Promise<{ deletedIds: string[] }> {
  const { data } = await api.post('/admin/waitlist/delete', { ids })
  return data.data
}

export interface AdminShopMember {
  membershipId: string
  userId: string
  name: string
  email: string
  phone?: string | null
  role: 'owner' | 'admin' | 'tech' | 'read'
  joinedAt: string
  /** Unix seconds of the user's last genuine login; null if never recorded. Never moved by admin impersonation. */
  lastLoginAt: number | null
  inactive: boolean
}

export interface AdminShopOverviewItem {
  id: string
  name: string
  email?: string | null
  phone?: string | null
  supportTier: 'standard' | 'premium'
  createdAt: string
  memberCount: number
  members: AdminShopMember[]
}

export interface AdminUserWithoutShop {
  id: string
  email: string
  name: string | null
  createdAt: string
  /** Unix seconds of the user's last genuine login; null if never recorded. Never moved by admin impersonation. */
  lastLoginAt: number | null
}

export interface AdminOverview {
  waitlist: {
    total: number
    marketingOptInTotal: number
    items: AdminWaitlistEntry[]
  }
  usersWithoutShop: {
    total: number
    items: AdminUserWithoutShop[]
  }
  shops: {
    total: number
    items: AdminShopOverviewItem[]
  }
}

export async function fetchAdminOverview(): Promise<AdminOverview> {
  const { data } = await api.get('/admin/overview')
  return data.data
}

export async function logInAsUser(userId: string): Promise<{
  redirectPath: string
  user: {
    id: string
    email: string
    name: string | null
  }
}> {
  const { data } = await api.post('/admin/impersonate', { userId })
  return data.data
}

export async function returnToAdminSession(): Promise<{
  redirectPath: string
}> {
  const { data } = await api.post('/auth/return-to-admin')
  return data.data
}

// --- Vehicle catalog editor ---

export interface AdminCatalogMake {
  id: string
  makeName: string
  active: boolean
}

export interface AdminCatalogModel {
  id: string
  modelName: string
  active: boolean
}

export async function fetchAdminCatalogYears(): Promise<number[]> {
  const { data } = await api.get('/admin/vehicle-catalog/years')
  return data.data
}

export async function fetchAdminCatalogMakes(year: number): Promise<AdminCatalogMake[]> {
  const { data } = await api.get(`/admin/vehicle-catalog/makes?year=${year}`)
  return data.data
}

export async function fetchAdminCatalogModels(year: number, make: string): Promise<AdminCatalogModel[]> {
  const { data } = await api.get(
    `/admin/vehicle-catalog/models?year=${year}&make=${encodeURIComponent(make)}`
  )
  return data.data
}

export async function setAdminCatalogMakeActive(id: string, active: boolean) {
  const { data } = await api.patch(`/admin/vehicle-catalog/makes/${id}`, { active })
  return data.data
}

export async function setAdminCatalogModelActive(id: string, active: boolean) {
  const { data } = await api.patch(`/admin/vehicle-catalog/models/${id}`, { active })
  return data.data
}
